import { z } from 'zod';
import { emailSchema, passwordSchema } from './auth.js';
const nameFields = z.object({
    firstName: z.string().trim().min(1).max(120),
    lastName: z.string().trim().min(1).max(120),
});
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Optional, asked alongside the registrant's own name/e-mail on both forms
 * below — never on `childFields`, a child in a "Elternkonto" registration
 * has neither a login of its own nor is old enough to be its own emergency
 * contact (see docs/members.md). Same field as `memberWritable`'s
 * `emergencyPhone`, kept separate here since this schema is public-facing
 * and doesn't otherwise depend on `member.ts`. Geburtstag is deliberately
 * not here — never asked of the registering parent, see the player variant's
 * own `birthDate` and `childFields.birthDate` below. */
const optionalPersonalFields = {
    emergencyPhone: z.string().trim().max(40).nullable().optional(),
};
/** A child registered alongside a parent — its own future player record,
 * not (yet) a login of its own. `birthDate` lives here rather than on
 * `optionalPersonalFields`: it's the child's age that matters for team/
 * training-group eligibility, not the registering parent's — see
 * docs/members.md. `birthDate` is required (auf Wunsch des Vereins:
 * Alterseinordnung braucht ein Geburtsdatum). `trainingGroupId` is optional
 * *here* on the wire — `InvitesService` enforces it as required whenever the
 * organisation actually has an active training group to choose from, and
 * only then (see `assertTrainingGroupProvided`); a club that hasn't set any
 * up yet must not lock itself out of self-registration entirely. `teamId`
 * stays unconditionally optional, eine Mannschaft lässt sich auch später
 * noch zuordnen. */
const childFields = nameFields.extend({
    birthDate: isoDate,
    teamId: z.string().uuid().optional(),
    trainingGroupId: z.string().uuid().optional(),
});
/**
 * Self-registration always picks one of two account types first (see
 * InvitePage.tsx): **Spielerkonto** (the registrant is the player) or
 * **Elternkonto** (the registrant is a parent/guardian registering on
 * behalf of one child — `member_guardians`, see docs/members.md). The org
 * itself is never part of either shape — it's fixed by the invite token.
 *
 * `register`: firstName/lastName/email/password are fixed, non-configurable
 * requirements for this flow specifically (self-registration always needs a
 * working login) — everything else reuses the normal validators so it's
 * checked the same way as the admin-facing create form.
 */
export const inviteRegisterRequest = z.discriminatedUnion('accountType', [
    nameFields.extend({
        accountType: z.literal('player'),
        email: emailSchema,
        password: passwordSchema,
        // Geburtstag: Pflichtfeld auf Wunsch des Vereins (Alterseinordnung).
        // Trainingsgruppe: siehe childFields' Kommentar oben — nur dann vom
        // Server erzwungen, wenn der Verein überhaupt eine aktive Gruppe hat.
        // Mannschaft bleibt optional (lässt sich auch später noch zuordnen).
        birthDate: isoDate,
        teamId: z.string().uuid().optional(),
        trainingGroupId: z.string().uuid().optional(),
        ...optionalPersonalFields,
    }),
    nameFields.extend({
        accountType: z.literal('parent'),
        email: emailSchema,
        password: passwordSchema,
        child: childFields,
        ...optionalPersonalFields,
    }),
]);
/** POST /public/invites/:token/join — same shape as `register`, for the case
 * where `register` came back `409 email_taken`: the email already has an
 * account (e.g. from another organisation), so instead of setting a new
 * password this authenticates the *existing* one — proof of ownership
 * before attaching a new member record in this org to that account. No
 * `passwordSchema` here on purpose: this checks an existing password
 * against whatever policy was in force when it was set, not the current
 * new-account policy. */
export const inviteJoinRequest = z.discriminatedUnion('accountType', [
    nameFields.extend({
        accountType: z.literal('player'),
        email: emailSchema,
        password: z.string().min(1),
        birthDate: isoDate,
        teamId: z.string().uuid().optional(),
        trainingGroupId: z.string().uuid().optional(),
        ...optionalPersonalFields,
    }),
    nameFields.extend({
        accountType: z.literal('parent'),
        email: emailSchema,
        password: z.string().min(1),
        child: childFields,
        ...optionalPersonalFields,
    }),
]);
//# sourceMappingURL=invite.js.map